/*
 * Copyright 2020 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */ import { isIOS, runAfterTransition } from '../utils';
import { getOwnerDocument } from '../focus/utils';
// Note that state only matters here for iOS. Non-iOS gets user-select: none applied to the target element
// rather than at the document level so we just need to apply/remove user-select: none for each pressed element individually
let state = 'default';
let savedUserSelect = '';
const modifiedElementMap = new WeakMap();
export function disableTextSelection(target) {
    if (isIOS()) {
        if (state === 'default') {
            const documentObject = getOwnerDocument(target);
            savedUserSelect = documentObject.documentElement.style.webkitUserSelect;
            documentObject.documentElement.style.webkitUserSelect = 'none';
        }
        state = 'disabled';
    } else if (target instanceof HTMLElement || target instanceof SVGElement) {
        // If not iOS, store the target's original user-select and change to user-select: none
        // Ignore state since it doesn't apply for non iOS
        modifiedElementMap.set(target, target.style.userSelect);
        target.style.userSelect = 'none';
    }
}
export function restoreTextSelection(target) {
    if (isIOS()) {
        // If the state is already default, there's nothing to do.
        // If it is restoring, then there's no need to queue a second restore.
        if (state !== 'disabled') {
            return;
        }
        state = 'restoring';
        // There appears to be a delay on iOS where selection still might occur
        // after pointer up, so wait a bit before removing user-select.
        setTimeout(()=>{
            // Wait for any CSS transitions to complete so we don't recompute style
            // for the whole page in the middle of the animation and cause jank.
            runAfterTransition(()=>{
                // Avoid race conditions
                if (state === 'restoring') {
                    const documentObject = getOwnerDocument(target);
                    if (documentObject.documentElement.style.webkitUserSelect === 'none') {
                        documentObject.documentElement.style.webkitUserSelect = savedUserSelect || '';
                    }
                    savedUserSelect = '';
                    state = 'default';
                }
            });
        }, 300);
    } else if (target instanceof HTMLElement || target instanceof SVGElement) {
        // If not iOS, restore the target's original user-select if any
        // Ignore state since it doesn't apply for non iOS
        if (target && modifiedElementMap.has(target)) {
            const targetOldUserSelect = modifiedElementMap.get(target);
            if (target.style.userSelect === 'none') {
                target.style.userSelect = targetOldUserSelect;
            }
            if (target.getAttribute('style') === '') {
                target.removeAttribute('style');
            }
            modifiedElementMap.delete(target);
        }
    }
}

//# sourceMappingURL=textSelection.js.map